import { useCallback, useRef, useState } from 'react';
import {
  AbortStreamFunction,
  ChatMessageChunk,
  ChatMessageChunkType,
  EntityContext,
  useLlmChatService,
} from 'nekst-api';
import { ChatMessage, ChatSessionContextValue } from './types';

function generateMessageId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function createUserMessage(content: string, context?: EntityContext[]): ChatMessage {
  return {
    id: generateMessageId(),
    role: 'user',
    content,
    timestamp: new Date(),
    context: context && context.length > 0 ? context : undefined,
  };
}

function createAssistantMessage(content: string): ChatMessage {
  return {
    id: generateMessageId(),
    role: 'assistant',
    content,
    timestamp: new Date(),
  };
}

function createToolActivityMessage(displayMessage: string): ChatMessage {
  return {
    id: generateMessageId(),
    role: 'tool-activity',
    content: displayMessage,
    timestamp: new Date(),
  };
}

function createErrorMessage(error: string): ChatMessage {
  return {
    id: generateMessageId(),
    role: 'assistant',
    content: error,
    timestamp: new Date(),
    isError: true,
  };
}

export function useChatSession(): ChatSessionContextValue {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isToolExecuting, setIsToolExecuting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [selectedContext, setSelectedContext] = useState<EntityContext[]>([]);

  const abortRef = useRef<AbortStreamFunction | null>(null);
  const conversationIdRef = useRef<number | null>(null);
  const streamingTextRef = useRef('');

  const chatService = useLlmChatService();

  const finalizeStreamedMessage = useCallback(() => {
    const completedText = streamingTextRef.current;
    if (completedText) {
      setMessages((prev) => [...prev, createAssistantMessage(completedText)]);
    }
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
  }, []);

  const handleChunk = useCallback((chunk: ChatMessageChunk) => {
    if (chunk.type === ChatMessageChunkType.TEXT_DELTA && chunk.text) {
      setIsToolExecuting(false);
      streamingTextRef.current += chunk.text;
      setStreamingText(streamingTextRef.current);
    } else if (chunk.type === ChatMessageChunkType.CONVERSATION_CREATED && chunk.conversation) {
      conversationIdRef.current = chunk.conversation.id;
      setCurrentConversationId(chunk.conversation.id);
    } else if (chunk.type === ChatMessageChunkType.TOOL_CALL && chunk.displayMessage) {
      setIsToolExecuting(true);
      const pendingText = streamingTextRef.current;
      if (pendingText) {
        streamingTextRef.current = '';
        setStreamingText('');
        setMessages((prev) => [
          ...prev,
          createAssistantMessage(pendingText),
          createToolActivityMessage(chunk.displayMessage!),
        ]);
      } else {
        setMessages((prev) => [...prev, createToolActivityMessage(chunk.displayMessage!)]);
      }
    } else if (chunk.type === ChatMessageChunkType.FINISH) {
      setIsToolExecuting(false);
      finalizeStreamedMessage();
    } else if (chunk.type === ChatMessageChunkType.ERROR) {
      setIsToolExecuting(false);
      setMessages((prev) => [...prev, createErrorMessage(chunk.error || 'An error occurred')]);
      streamingTextRef.current = '';
      setStreamingText('');
      setIsStreaming(false);
    }
  }, [finalizeStreamedMessage]);

  const sendMessage = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || isStreaming) {
      return;
    }

    const contextToSend = selectedContext.length > 0 ? selectedContext : undefined;
    setMessages((prev) => [...prev, createUserMessage(trimmed, contextToSend)]);
    setInputText('');
    setIsStreaming(true);
    streamingTextRef.current = '';
    setStreamingText('');

    if (conversationIdRef.current) {
      abortRef.current = chatService.sendMessage(
        conversationIdRef.current,
        { message: trimmed, context: contextToSend },
        handleChunk,
      );
    } else {
      abortRef.current = chatService.startConversation(
        { message: trimmed, context: contextToSend },
        handleChunk,
      );
    }
  }, [inputText, isStreaming, selectedContext, chatService, handleChunk]);

  const resetConversation = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    setMessages([]);
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
    setIsToolExecuting(false);
    setInputText('');
    setCurrentConversationId(null);
    conversationIdRef.current = null;
    setSelectedContext([]);
  }, []);

  const addContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) => {
      const exists = prev.some((c) => c.type === context.type && c.id === context.id);
      if (exists) {
        return prev;
      }
      return [...prev, context];
    });
  }, []);

  const removeContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) =>
      prev.filter((c) => !(c.type === context.type && c.id === context.id)),
    );
  }, []);

  const clearContext = useCallback(() => {
    setSelectedContext([]);
  }, []);

  return {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
    inputText,
    currentConversationId,
    selectedContext,
    sendMessage,
    resetConversation,
    setInputText,
    addContext,
    removeContext,
    clearContext,
  };
}
